/**
 * Web Push enrolment for ISG apps.
 *
 * Wraps the browser's permission + PushManager dance and the ISG Notify
 * push proxy (`/api/push/*`) the host app exposes. All HTTP goes through
 * the shared `api` client so cookie auth and CSRF are handled — never
 * hand-roll a fetch here.
 *
 * Web Push is per-origin: enabling it on the intranet does nothing for
 * the helpdesk and vice versa. Each app mounts its own control.
 */
import { api } from './api.js';

/**
 * iOS/iPadOS only delivers Web Push to a site the user has added to the
 * Home Screen — permission cannot even be requested from a normal Safari
 * tab. Detecting that lets the UI show install instructions instead of a
 * button that silently does nothing.
 */
function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * Does an existing subscription use the given applicationServerKey?
 *
 * `options.applicationServerKey` is an ArrayBuffer the browser echoes
 * back. Older browsers may not expose `options` at all — treat that as
 * "can't prove it matches" and re-subscribe, which is the safe way to
 * be wrong.
 */
function subscriptionUsesKey(sub, keyBytes) {
  const existing = sub.options && sub.options.applicationServerKey;
  if (!existing) return false;
  const a = new Uint8Array(existing);
  if (a.length !== keyBytes.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== keyBytes[i]) return false;
  }
  return true;
}

/** True when this browser can do Web Push at all. */
export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window && // i18n-ignore: browser API name, not text
    'Notification' in window // i18n-ignore: browser API name, not text
  );
}

async function getRegistration() {
  // `ready` never rejects but can hang if no worker is registering —
  // vite-plugin-pwa's autoUpdate registers one on load, so in practice
  // this resolves. The race keeps a broken install from freezing the UI.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(() => resolve(null), 10000)),
  ]);
}

/**
 * Current push state for rendering a control.
 *
 * @returns {Promise<{supported, configured, permission, subscribed,
 *                    requiresInstall, isIos, standalone}>}
 */
export async function getPushState() {
  const base = {
    supported: pushSupported(),
    configured: false,
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'default',
    subscribed: false,
    isIos: isIos(),
    standalone: isStandalone(),
    // iPhone/iPad in a browser tab: the user must install first.
    requiresInstall: isIos() && !isStandalone(),
  };

  if (!base.supported) return base;

  try {
    const cfg = await api.get('/push/vapid-key');
    base.configured = Boolean(cfg?.configured);
  } catch (e) {
    base.configured = false;
  }

  try {
    const reg = await getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    base.subscribed = Boolean(sub);
  } catch (e) {
    base.subscribed = false;
  }

  return base;
}

/**
 * Get a subscription for `reg` that is bound to `publicKey`, replacing
 * any existing one that isn't.
 *
 * ⚠️ A PushSubscription is cryptographically bound to the
 * applicationServerKey it was created with. Reusing one made under a
 * different key (an earlier experiment, a rotated keypair) looks fine
 * in the browser and then fails at the push service with 403/410 — which
 * the server reads as "endpoint gone" and prunes. The user sees
 * "enabled", a failed test, and an empty device list, with nothing in
 * the UI explaining why. Re-subscribing with a different key also throws
 * InvalidStateError, so a mismatched subscription must be explicitly
 * unsubscribed first rather than simply overwritten.
 */
async function subscribeWithKey(reg, publicKey) {
  const appServerKey = urlBase64ToUint8Array(publicKey);

  let sub = await reg.pushManager.getSubscription();
  if (sub && !subscriptionUsesKey(sub, appServerKey)) {
    try {
      await sub.unsubscribe();
    } catch (e) {
      /* fall through — subscribe() below surfaces any real problem */
    }
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    });
  }
  return sub;
}

/**
 * Ask for permission and enrol this browser.
 *
 * @param {string} appSlug Origin app ('intranet', 'helpdesk') — recorded
 *   against the subscription so the delivery log can say where it came
 *   from. Not a preference: those stay per-app in ISG Notify.
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function enablePush(appSlug) {
  if (!pushSupported()) {
    return { ok: false, reason: 'unsupported' };
  }
  if (isIos() && !isStandalone()) {
    return { ok: false, reason: 'requires_install' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: permission === 'denied' ? 'denied' : 'dismissed' };
  }

  const cfg = await api.get('/push/vapid-key');
  if (!cfg?.public_key) {
    return { ok: false, reason: 'not_configured' };
  }

  const reg = await getRegistration();
  if (!reg) return { ok: false, reason: 'no_service_worker' };

  const sub = await subscribeWithKey(reg, cfg.public_key);

  await api.post('/push/subscribe', {
    app_slug: appSlug,
    subscription: sub.toJSON(),
  });

  return { ok: true };
}

/** Unenrol this browser and drop the server-side row. */
export async function disablePush() {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };

  const reg = await getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (!sub) return { ok: true };

  const endpoint = sub.endpoint;
  // Unsubscribe locally first: if the server call fails we'd rather hold
  // a stale row (pruned on its next 410) than leave the browser
  // subscribed to a push nobody will send.
  try {
    await sub.unsubscribe();
  } catch (e) {
    /* fall through — still tell the server to forget it */
  }
  await api.post('/push/unsubscribe', { endpoint });
  return { ok: true };
}

/** Send a test push to this user's own devices. */
export async function sendTestPush(appSlug) {
  return api.post('/push/test', { app_slug: appSlug });
}

/**
 * Reconcile the browser's subscription with the server's record.
 *
 * Push services rotate endpoints, and the service worker's
 * `pushsubscriptionchange` re-registration can't always send a CSRF
 * token. Calling this on app boot is the reliable repair: if this
 * browser holds a subscription, re-POST it (the server upserts on
 * endpoint, so a repeat is free and a rotated one is healed).
 *
 * Silent by design — it must never interrupt boot.
 */
export async function syncPushSubscription(appSlug) {
  if (!pushSupported()) return;
  if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') return;

  try {
    const reg = await getRegistration();
    if (!reg) return;

    const existing = await reg.pushManager.getSubscription();
    if (!existing) return;

    // Re-subscribe if the stored subscription is bound to a different
    // VAPID key than the server now serves. Without this a device whose
    // subscription was pruned for a key mismatch stays permanently
    // broken: the browser still reports itself subscribed, so the UI
    // shows push as ON and never offers the Enable button that would
    // repair it.
    const cfg = await api.get('/push/vapid-key');
    if (!cfg?.public_key) return;

    const sub = await subscribeWithKey(reg, cfg.public_key);
    await api.post('/push/subscribe', {
      app_slug: appSlug,
      subscription: sub.toJSON(),
    });
  } catch (e) {
    /* best-effort */
  }
}
